<script setup lang="ts">
import { motion } from 'motion-v'

const { t, tm, rt } = useI18n()

useHead({
  link: [{
    rel: 'canonical',
    href: `${import.meta.env.VITE_MONOLITH_URL}/contact-us/existing-customer`,
  }],
})

useSeoMeta({
  title: t('contact_us/existing_customer.meta.title'),
  description: t('contact_us/existing_customer.meta.description'),

  ogTitle: t('contact_us/existing_customer.meta.title'),
  ogDescription: t('contact_us/existing_customer.meta.description'),
  ogImage: `${S3_ASSETS_URL}/meta-default.jpg`,
})

const captchaService = useScript({
  src: 'https://www.google.com/recaptcha/api.js?render=explicit',
  async: true,
  defer: true,
  crossorigin: false,
}, {
  use: () => window.grecaptcha,
})

const captchaElement = useTemplateRef('captcha')

const [isFormExpanded, toggleForm] = useToggle(false)

const isFormSubmitted = ref(false)

const isScreenLG = useMediaQuery('(min-width: 1024px)')

const form = useForm({
  defaultValues: {
    spot_id: '',
    representative: '',
    first_name: '',
    last_name: '',
    spot_name: '',
    restaurant_metro_area: '',
    restaurant_address: '',
    email: '',
    phone: '',
    time_window: 'anytime',
    message: '',
    captcha: '',
  },
  validators: {
    onSubmit: existingCustomerValidationSchema,
  },
  onSubmit: async ({ value }) => {
    try {
      const data = new FormData()

      data.append('purpose', 'customer')

      Object.entries(value).forEach(([key, value]) => {
        switch (key) {
          case 'representative':
            return data.append('customer_success_rep', String(value))

          default:
            data.append(key, String(value))
        }
      })

      const response = await useRequest('/contact-us')
        .post(data)
        .text()

      if (response.statusCode.value !== 200) {
        return Promise.reject({
          message: 'Something went wrong while submitting your request.',
        })
      }

      window.gtag('event', 'existing_customer')

      form.reset()
      captchaService.proxy.reset()
      isFormSubmitted.value = true
    }
    catch (error) {
      captchaService.proxy.reset()
      console.error(error)
    }
  },
})

captchaService.proxy.ready(() => {
  if (!captchaElement.value) return

  captchaService.proxy.render(captchaElement.value, {
    sitekey: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
    size: 'compact',
    callback: (value) => {
      form.setFieldValue('captcha', value)
    },
  })

  // Handle edge-case for screen-reader
  const field = captchaElement.value.querySelector('textarea')
  field?.setAttribute('aria-hidden', 'true')
  field?.setAttribute('aria-readonly', 'true')
  field?.setAttribute('aria-label', 'reCaptcha Validation')
})
</script>

<template>
  <div class="isolate flex min-h-dvh flex-col bg-[#F8F8F0]">
    <LayoutNavbarAlt />

    <section class="mx-auto grid max-w-screen-xl place-items-start gap-8 px-2 pb-8 lg:grid-cols-2 lg:px-8 lg:py-16">
      <ContactPartialCustomerContext
        v-if="isScreenLG"
        :is-form-expanded="isFormExpanded"
      />

      <div class="flex w-full flex-col rounded-2xl bg-gray-600/5">
        <LayoutContactNavbar />

        <ContactPartialCustomerContext
          v-if="!isScreenLG"
          :is-form-expanded="isFormExpanded"
          class="!gap-y-4 p-5 text-center"
        />

        <div class="overflow-clip rounded-2xl border border-gray-200 bg-white p-4 lg:p-8">
          <AnimatePresence>
            <motion.div
              v-if="!isFormExpanded"
              class="grid gap-y-5 overflow-hidden"
              :initial="{ opacity: 0, height: 0 }"
              :animate="{ opacity: 1, height: 'auto' }"
              :exit="{ opacity: 0, height: 0 }"
            >
              <motion.div
                class="grid place-items-start gap-y-5 rounded-xl bg-neutral-100 p-8"
                layout-id="customer-login"
              >
                <UiIcon
                  name="headset-mic-outline"
                  class="text-4xl text-orange-500"
                />

                <header>
                  <h4 class="text-xl font-semibold leading-loose">
                    {{ t('contact_us/existing_customer.cards.support.title') }}
                  </h4>

                  <p>
                    {{ t('contact_us/existing_customer.cards.support.description') }}
                  </p>
                </header>

                <UiButton as-child>
                  <RouterLink
                    class="group"
                    :to="{ name: '/login' }"
                  >
                    {{ t('contact_us/existing_customer.cards.support.action') }}

                    <UiIcon
                      name="arrow-forward-rounded"
                      class="transition-transform duration-200 group-hover:translate-x-1"
                    />
                  </RouterLink>
                </UiButton>
              </motion.div>

              <div class="grid place-items-start gap-y-5 px-8">
                <header>
                  <h4 class="text-xl font-semibold leading-loose">
                    {{ t('contact_us/existing_customer.cards.contact.title') }}
                  </h4>

                  <p>
                    {{ t('contact_us/existing_customer.cards.contact.description') }}
                  </p>
                </header>

                <UiButton
                  type="button"
                  color="neutral"
                  class="group"
                  @click="toggleForm"
                >
                  {{ t('contact_us/existing_customer.cards.contact.action') }}

                  <UiIcon
                    name="arrow-forward-rounded"
                    class="transition-transform duration-200 group-hover:translate-x-1"
                  />
                </UiButton>
              </div>
            </motion.div>

            <motion.div
              v-show="isFormExpanded"
              class="grid gap-y-5 overflow-hidden"
              :initial="{ opacity: 0, height: 0 }"
              :animate="{ opacity: 1, height: 'auto' }"
              :exit="{ opacity: 0, height: 0 }"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  v-if="isFormSubmitted"
                  class="grid gap-y-5 text-balance text-center"
                  :initial="{ height: 0, opacity: 0 }"
                  :animate="{ height: 'auto', opacity: 1 }"
                  :exit="{ height: 0, opacity: 0 }"
                >
                  <img
                    :src="`${S3_ASSETS_URL}/contact/success-illustration.svg`"
                    class="mx-auto max-w-32"
                  >

                  <strong class="text-4xl font-medium">
                    {{ t('contact_us/existing_customer.form.success.title') }}
                  </strong>

                  <p class="text-lg">
                    <span
                      v-for="(message, messageIndex) in tm('contact_us/existing_customer.form.success.messages')"
                      :key="messageIndex"
                      class="block"
                    >
                      {{ rt(message) }}
                    </span>
                  </p>
                </motion.div>

                <motion.div
                  v-else
                  :initial="{ height: 0, opacity: 0 }"
                  :animate="{ height: 'auto', opacity: 1 }"
                  :exit="{ height: 0, opacity: 0 }"
                >
                  <h4 class="text-balance text-center text-lg font-semibold lg:text-left">
                    {{ t('contact_us/existing_customer.form.title') }}
                  </h4>

                  <form
                    class="mt-5 grid grid-cols-2 gap-5"
                    @submit.prevent="form.handleSubmit"
                  >
                    <div class="col-span-2 grid auto-cols-auto grid-flow-col gap-5">
                      <form.Field
                        name="spot_id"
                        :validators="{
                          onChange: idSchema,
                        }"
                      >
                        <template #default="{ field }">
                          <UiField
                            :id="field.name"
                            type="text"
                            :label="'Your Spot ID'"
                            placeholder="123456"
                            autocomplete="off"
                            container-class="max-w-24 min-w-full"
                            :value="field.state.value"
                            @blur="field.handleBlur"
                            @input="
                              (e) => field.handleChange(e.target.value)
                            "
                          />
                        </template>
                      </form.Field>

                      <form.Field
                        name="representative"
                        :validators="{
                          onChange: nameSchema,
                        }"
                      >
                        <template #default="{ field }">
                          <UiField
                            :id="field.name"
                            type="text"
                            :label="'Your dedicated customer success representative'"
                            placeholder="Danny Thompson"
                            autocomplete="off"
                            required
                            :value="field.state.value"
                            :error="field.state.meta.errors[0]?.message"
                            @blur="field.handleBlur"
                            @input="
                              (e) => field.handleChange(e.target.value)
                            "
                          />
                        </template>
                      </form.Field>
                    </div>

                    <form.Field
                      name="first_name"
                      :validators="{
                        onChange: nameSchema,
                      }"
                    >
                      <template #default="{ field }">
                        <UiField
                          :id="field.name"
                          type="text"
                          :label="t('contact_us/existing_customer.form.fields.first_name')"
                          placeholder="James"
                          autocomplete="given-name"
                          container-class="col-span-2 lg:col-span-1 w-full"
                          required
                          :value="field.state.value"
                          :error="field.state.meta.errors[0]?.message"
                          @blur="field.handleBlur"
                          @input="
                            (e) => field.handleChange(e.target.value)
                          "
                        />
                      </template>
                    </form.Field>

                    <form.Field
                      name="last_name"
                      :validators="{
                        onChange: nameSchema,
                      }"
                    >
                      <template #default="{ field }">
                        <UiField
                          :id="field.name"
                          type="text"
                          :label="t('contact_us/existing_customer.form.fields.last_name')"
                          placeholder="Smith"
                          autocomplete="family-name"
                          container-class="col-span-2 lg:col-span-1 w-full"
                          required
                          :value="field.state.value"
                          :error="field.state.meta.errors[0]?.message"
                          @blur="field.handleBlur"
                          @input="
                            (e) => field.handleChange(e.target.value)
                          "
                        />
                      </template>
                    </form.Field>

                    <form.Field
                      name="email"
                      :validators="{
                        onChange: emailSchema,
                      }"
                    >
                      <template #default="{ field }">
                        <UiField
                          :id="field.name"
                          type="email"
                          :label="t('contact_us/existing_customer.form.fields.email')"
                          placeholder="james@acme.com"
                          autocomplete="email"
                          container-class="col-span-2 lg:col-span-1 w-full"
                          required
                          :value="field.state.value"
                          :error="field.state.meta.errors[0]?.message"
                          @blur="field.handleBlur"
                          @input="
                            (e) => field.handleChange(e.target.value)
                          "
                        />
                      </template>
                    </form.Field>

                    <form.Field
                      name="phone"
                      :validators="{
                        onChange: phoneSchema,
                      }"
                    >
                      <template #default="{ field }">
                        <UiField
                          :id="field.name"
                          type="tel"
                          :label="t('contact_us/existing_customer.form.fields.phone')"
                          placeholder="+123456789"
                          autocomplete="tel"
                          container-class="col-span-2 lg:col-span-1 w-full"
                          required
                          :value="field.state.value"
                          :error="field.state.meta.errors[0]?.message"
                          @blur="field.handleBlur"
                          @input="
                            (e) => field.handleChange(e.target.value)
                          "
                        />
                      </template>
                    </form.Field>

                    <form.Field
                      name="spot_name"
                      :validators="{
                        onChange: nameSchema,
                      }"
                    >
                      <template #default="{ field }">
                        <UiField
                          :id="field.name"
                          type="text"
                          :label="t('contact_us/existing_customer.form.fields.spot_name')"
                          placeholder="Acme Foods"
                          autocomplete="off"
                          container-class="col-span-2 lg:col-span-1 w-full"
                          required
                          :value="field.state.value"
                          :error="field.state.meta.errors[0]?.message"
                          @blur="field.handleBlur"
                          @input="
                            (e) => field.handleChange(e.target.value)
                          "
                        />
                      </template>
                    </form.Field>

                    <form.Field
                      name="restaurant_metro_area"
                      :validators="{
                        onChange: nameSchema,
                      }"
                    >
                      <template #default="{ field }">
                        <UiField
                          :id="field.name"
                          type="text"
                          :label="t('contact_us/existing_customer.form.fields.restaurant_metro_area')"
                          placeholder="Springfield"
                          autocomplete="off"
                          container-class="col-span-2 lg:col-span-1 w-full"
                          required
                          :value="field.state.value"
                          :error="field.state.meta.errors[0]?.message"
                          @blur="field.handleBlur"
                          @input="
                            (e) => field.handleChange(e.target.value)
                          "
                        />
                      </template>
                    </form.Field>

                    <form.Field
                      name="restaurant_address"
                      :validators="{
                        onChange: nameSchema,
                      }"
                    >
                      <template #default="{ field }">
                        <UiField
                          :id="field.name"
                          type="text"
                          :label="t('contact_us/existing_customer.form.fields.restaurant_address')"
                          placeholder="123 Main St, Springfield, IL 62704"
                          autocomplete="off"
                          container-class="col-span-2 w-full"
                          required
                          :value="field.state.value"
                          :error="field.state.meta.errors[0]?.message"
                          @blur="field.handleBlur"
                          @input="
                            (e) => field.handleChange(e.target.value)
                          "
                        />
                      </template>
                    </form.Field>

                    <form.Field
                      name="time_window"
                      :validators="{
                        onChange: timeWindowSchema,
                      }"
                    >
                      <template #default="{ field }">
                        <UiSelect
                          :id="field.name"
                          :label="t('contact_us/existing_customer.form.fields.time_window')"
                          placeholder="Please select"
                          autocomplete="off"
                          container-class="col-span-2 w-full"
                          :items="contactTimeFrames"
                          :model-value="field.state.value"
                          :error="field.state.meta.errors[0]?.message"
                          @blur="field.handleBlur"
                          @update:model-value="
                            (value) => field.handleChange(value)
                          "
                        />
                      </template>
                    </form.Field>

                    <form.Field
                      name="message"
                      :validators="{
                        onChange: messageSchema,
                      }"
                    >
                      <template #default="{ field }">
                        <UiField
                          :id="field.name"
                          type="text"
                          :label="t('contact_us/existing_customer.form.fields.message')"
                          placeholder="Type your message here..."
                          autocomplete="off"
                          container-class="col-span-2 w-full"
                          class="min-h-24"
                          :value="field.state.value"
                          :error="field.state.meta.errors[0]?.message"
                          as-child
                          @blur="field.handleBlur"
                          @input="
                            (e) => field.handleChange(e.target.value)
                          "
                        >
                          <textarea />
                        </UiField>
                      </template>
                    </form.Field>

                    <div class="col-span-2 grid auto-cols-auto gap-x-2 gap-y-4 lg:grid-flow-col">
                      <form.Field
                        name="captcha"
                        :validators="{
                          onChange: captchaSchema,
                        }"
                      >
                        <template #default="{ field }">
                          <div class="text-center">
                            <div
                              ref="captcha"
                              class="flex items-center justify-center"
                            />

                            <AnimatePresence>
                              <motion.span
                                v-if="field.state.meta.errors.length"
                                :initial="{ opacity: 0, scale: 0.8 }"
                                :animate="{ opacity: 1, scale: 1 }"
                                :exit="{ opacity: 0, scale: 0.8 }"
                                :transition="{
                                  type: 'spring',
                                  stiffness: 400,
                                  damping: 25,
                                  duration: 0.4,
                                }"
                                class="shrink-0 text-xs text-red-500 dark:text-red-600"
                                role="alert"
                              >
                                {{ field.state.meta.errors[0]?.message }}
                              </motion.span>
                            </AnimatePresence>
                          </div>
                        </template>
                      </form.Field>
                    </div>

                    <form.Subscribe>
                      <template #default="ctx">
                        <UiButton
                          type="submit"
                          class="group col-span-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:grayscale"
                          :disabled="!ctx.canSubmit"
                        >
                          <motion.span
                            v-show="ctx.isSubmitting"
                            class="inline-flex origin-right text-xl text-white"
                            :initial="{ opacity: 0, scale: 0 }"
                            :animate="{ opacity: 1, scale: 1 }"
                            :exit="{ opacity: 0, scale: 0 }"
                            :transition="{
                              type: 'spring',
                              stiffness: 200,
                              damping: 20,
                            }"
                          >
                            <UiIcon
                              name="loader"
                              class="animate-spin"
                            />
                          </motion.span>

                          {{ t('contact_us/existing_customer.form.fields.action') }}

                          <motion.span
                            v-show="!ctx.isSubmitting"
                            class="inline-flex origin-left text-xl text-white"
                            :initial="{ opacity: 0, scale: 0 }"
                            :animate="{ opacity: 1, scale: 1 }"
                            :exit="{ opacity: 0, scale: 0 }"
                            :transition="{
                              type: 'spring',
                              stiffness: 200,
                              damping: 20,
                            }"
                          >
                            <UiIcon
                              name="arrow-forward-rounded"
                              class="transition-transform duration-200 group-hover:translate-x-1"
                            />
                          </motion.span>
                        </UiButton>
                      </template>
                    </form.Subscribe>
                  </form>
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </AnimatePresence>
        </div>

        <div class="p-5">
          <p class="text-xs">
            <i18n-t keypath="contact_us/existing_customer.legal.notice">
              <template #privacy_placeholder>
                <RouterLink
                  :to="{ name: '/legal/privacy-policy' }"
                  class="font-medium text-orange-500 underline underline-offset-1 transition-colors hover:text-orange-600"
                >
                  {{ t('contact_us/existing_customer.legal.privacy_placeholder') }}
                </RouterLink>
              </template>

              <template #terms_placeholder>
                <RouterLink
                  :to="{ name: '/legal/terms-of-service' }"
                  class="font-medium text-orange-500 underline underline-offset-1 transition-colors hover:text-orange-600"
                >
                  {{ t('contact_us/existing_customer.legal.terms_placeholder') }}
                </RouterLink>
              </template>
            </i18n-t>
          </p>
        </div>
      </div>
    </section>
  </div>
</template>
